import React, { useContext, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faEye,
  faChartLine,
  faCalendarAlt,
  faFilter,
  faSearch,
  faDollarSign,
  faRupeeSign,
  faFileExport,
  faBullseye,
} from "@fortawesome/free-solid-svg-icons";
import apiService from "../../ApiService";
import Select from "react-select";
import DataCategory from "../../Data/CategoryMapping";
import IndCategoryMapping from "../../Data/IndCategoryMapping";
import PersonWeightsForm from "../bank/PersonWeightsForm";
import MonthlyCorpsForm from "../bank/MonthlyCorpsForm";
import UpdateTransactionCategory from "./UpdateTransactionCategory";
import UpdateExpenseCategory from "./UpdateExpenseCategory";
import UserContext from "../../contexts/UserContext";
import SummaryView from "../bank/SummaryView";
import GoalsManagement from "../bank/GoalsManagement";

// Component for financial transaction summary analysis

const yearOptions = Array.from({ length: 6 }, (_, i) => {
  const y = new Date().getFullYear() - i;
  return { value: String(y), label: String(y) };
});

// Helper to get actual category name from mapping
function getActualCategory(internalName, value) {
  if (!DataCategory || !DataCategory[0]) return value || "-";
  const arr = DataCategory[0][internalName];
  if (!arr || arr.length === 0) return value || "-";
  // If more than one mapped value, return comma-separated string
  if (arr.length > 1) return arr.join(", ");
  return arr[0];
}
function getActualCategoryInd(internalName, value) {
  if (!IndCategoryMapping || !IndCategoryMapping[0]) return value || "-";
  const arr = IndCategoryMapping[0][internalName];
  if (!arr || arr.length === 0) return value || "-";
  // If more than one mapped value, return comma-separated string
  if (arr.length > 1) return arr.join(", ");
  return arr[0];
}

const TransactionSummaryByYears = () => {
  const { user } = useContext(UserContext);
  const [selectedTab, setSelectedTab] = useState("USA");
  const [selectedYears, setSelectedYears] = useState([yearOptions[0]]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalData, setModalData] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCompactCurrency, setShowCompactCurrency] = useState(true);
  const [monthlyInvestmentData, setMonthlyInvestmentData] = useState([]);

  // Simple back navigation using browser history
  const handleGoBack = () => {
    window.history.back();
  };
  // Helper to get first/last day of month
  const isAdminUser = () => {
    return user && user.role === "admin";
  };

  const getMonthRange = (year, month) => {
    const start = `${year}-${String(month).padStart(2, "0")}-01`;
    const endDate = new Date(year, month, 0).getDate();
    const end = `${year}-${String(month).padStart(2, "0")}-${endDate}`;
    return { start, end };
  };

  // Handler for cell click
  const handleCellClick = async (category, year, month) => {
    if (!category || !year || !month) return;
    setModalOpen(true);
    setModalTitle(`${category} - ${getMonthAbbr(month)} ${year}`);
    setModalLoading(true);
    const { start, end } = getMonthRange(year, month);
    try {
      let txRes = [],
        expRes = [];
      if (selectedTab === "USA" || selectedTab === "INDIA") {
        var ctry = selectedTab === "INDIA" ? "IND" : "USA";
        const categoryMappingFn =
          ctry === "IND" ? getActualCategoryInd : getActualCategory;

        [txRes, expRes] = await Promise.all([
          apiService.fetchTransactions({
            fromDate: start,
            toDate: end,
            pageIndex: 1,
            pageSize: 1000,
            country: ctry,
            category: categoryMappingFn(category),
          }),
          apiService.fetchExpenses({
            fromDate: start,
            toDate: end,
            pageIndex: 1,
            pageSize: 1000,
            country: ctry,
            category: categoryMappingFn(category),
          }),
        ]);
      } else if (selectedTab === "Health Record") {
        // Placeholder: fetch health records API here
        txRes = await apiService.fetchHealthRecords({
          fromDate: start,
          toDate: end,
        });
        expRes = [];
      } else if (selectedTab === "Investment Details") {
        // Placeholder: fetch investment details API here
        txRes = await apiService.fetchInvestmentDetails({
          fromDate: start,
          toDate: end,
        });
        expRes = [];
      }
      const txRecords = txRes.records || txRes.data || txRes || [];
      const expRecords = expRes.records || expRes.data || expRes || [];
      setModalData([...txRecords, ...expRecords]);
      if (selectedTab === "INDIA") {
        setModalData([...txRecords]);
      }
    } catch (e) {
      setModalData([]);
    }
    setModalLoading(false);
  };

  const fetchSummary = async () => {
    setLoading(true);
    var country = selectedTab === "INDIA" ? "IND" : "USA";
    try {
      const years = selectedYears.map((y) => y.value);
      const data = await apiService.fetchTransactionSummaryByYears(
        years,
        country,
      );
      setSummary(Array.isArray(data) ? data : data.records || data.data || []);
    } catch (e) {
      setSummary([]);
    }
    setLoading(false);
  };

  // Fetch monthly investment data
  const fetchMonthlyInvestmentData = async () => {
    try {
      const data = await apiService.getMonthlyCorps();
      setMonthlyInvestmentData(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to fetch monthly investment data:", e);
      setMonthlyInvestmentData([]);
    }
  };

  // Fetch monthly investment data on component mount
  useEffect(() => {
    fetchMonthlyInvestmentData();
  }, []);

  // Handler for View Analytics button
  const handleViewAnalytics = () => {
    // Scroll to the portfolio analytics section
    const analyticsSection = document.getElementById("portfolio-analytics");
    if (analyticsSection) {
      analyticsSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Handler for Export Data button
  const handleExportData = () => {
    if (!monthlyInvestmentData || monthlyInvestmentData.length === 0) {
      alert("No investment data available to export");
      return;
    }

    // Prepare CSV data
    const headers = [
      "Year",
      "Month",
      "India Stock (₹)",
      "India Bond (₹)",
      "USA Stock ($)",
      "USA Fixed ($)",
      "India Pension (₹)",
      "HSA ($)",
      "ICICI PruLife (₹)",
      "401K ($)",
    ];

    const csvRows = [
      headers.join(","),
      ...monthlyInvestmentData.map((row) =>
        [
          row.year,
          row.month,
          row.india_stock || 0,
          row.india_bond || 0,
          row.usa_stock || 0,
          row.usa_fixed || 0,
          row.india_pension || 0,
          row.hsa || 0,
          row.icici_prulife || 0,
          row.k_401k || 0,
        ].join(","),
      ),
    ];

    // Create and download CSV file
    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `investment_data_${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Function to get the latest investment portfolio data
  const getLatestPortfolioData = () => {
    if (!monthlyInvestmentData || monthlyInvestmentData.length === 0) {
      // Return empty values if no API data available
      return {
        indiaStock: 0,
        indiaBond: 0,
        usaStock: 0,
        usaFixed: 0,
        iciciprulife: 0,
      };
    }

    // Get the most recent investment data from API
    const latestData = monthlyInvestmentData.reduce((latest, current) => {
      const currentDate = new Date(current.year, current.month - 1);
      const latestDate = new Date(latest.year, latest.month - 1);
      return currentDate > latestDate ? current : latest;
    }, monthlyInvestmentData[0]);

    // Convert API data to the format expected by portfolio calculation
    // Only include: India Stock, USA Stock, USA Fixed, ICICI PruLife
    // Exclude: HSA, 401K, India Pension (all pension investments excluded)
    const exchangeRate = 83; // USD to INR conversion rate
    return {
      indiaStock: parseFloat(latestData.india_stock || 0) / 100000, // Convert from ₹ to Lakhs
      indiaBond: parseFloat(latestData.india_bond || 0) / 100000, // Convert from ₹ to Lakhs
      usaStock: (parseFloat(latestData.usa_stock || 0) * exchangeRate) / 100000, // Convert from $ to Lakhs
      usaFixed: (parseFloat(latestData.usa_fixed || 0) * exchangeRate) / 100000, // Convert from $ to Lakhs
      iciciprulife: parseFloat(latestData.icici_prulife || 0) / 100000, // Convert from ₹ to Lakhs
    };
  };

  // Function to get monthly trends data from API
  const getMonthlyTrends = () => {
    if (!monthlyInvestmentData || monthlyInvestmentData.length === 0) {
      // Return empty array if no API data available
      return [];
    }

    // Sort data by year and month (most recent first)
    const sortedData = [...monthlyInvestmentData]
      .sort((a, b) => {
        const dateA = new Date(a.year, a.month - 1);
        const dateB = new Date(b.year, b.month - 1);
        return dateB - dateA;
      })
      .slice(0, 6); // Get last 6 months

    const exchangeRate = 83;

    return sortedData.map((data, index) => {
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const monthStr = `${monthNames[data.month - 1]} ${data.year}`;

      // Calculate total in Crores (convert from actual rupees/dollars to Lakhs)
      // Only include: India Stock, USA Stock, USA Fixed, ICICI PruLife
      const totalInLakhs =
        parseFloat(data.india_stock || 0) / 100000 +
        parseFloat(data.india_bond || 0) / 100000 +
        (parseFloat(data.usa_stock || 0) * exchangeRate) / 100000 +
        (parseFloat(data.usa_fixed || 0) * exchangeRate) / 100000 +
        parseFloat(data.icici_prulife || 0) / 100000;

      const totalInCrores = (totalInLakhs / 100).toFixed(2);

      // Calculate growth (comparing with previous month if available)
      let growth = "+0%";
      let color = "bg-gray-500";

      if (index < sortedData.length - 1) {
        const prevData = sortedData[index + 1];
        const prevTotal =
          parseFloat(prevData.india_stock || 0) / 100000 +
          parseFloat(prevData.india_bond || 0) / 100000 +
          (parseFloat(prevData.usa_stock || 0) * exchangeRate) / 100000 +
          (parseFloat(prevData.usa_fixed || 0) * exchangeRate) / 100000 +
          parseFloat(prevData.icici_prulife || 0) / 100000;
        if (prevTotal > 0) {
          const growthPercent = (
            ((totalInLakhs - prevTotal) / prevTotal) *
            100
          ).toFixed(1);
          growth =
            growthPercent >= 0 ? `+${growthPercent}%` : `${growthPercent}%`;

          if (parseFloat(growthPercent) > 15) color = "bg-green-600";
          else if (parseFloat(growthPercent) > 5) color = "bg-green-500";
          else if (parseFloat(growthPercent) >= 0) color = "bg-green-400";
          else color = "bg-red-500";
        }
      }

      return {
        month: monthStr,
        indiaStock: `₹${(parseFloat(data.india_stock || 0) / 100000).toFixed(
          1,
        )}L`,
        indiaBond: `₹${(parseFloat(data.india_bond || 0) / 100000).toFixed(
          1,
        )}L`,
        usaStock: `$${(parseFloat(data.usa_stock || 0) / 1000).toFixed(1)}K`,
        usaFixed: `$${(parseFloat(data.usa_fixed || 0) / 1000).toFixed(1)}K`,
        total: `₹${totalInCrores}Cr`,
        growth,
        color,
      };
    });
  };

  // Add Month and Year as first columns
  const baseColumns = [
    { key: "year", label: "Year" },
    { key: "month", label: "Month" },
  ];
  const columnGroups = [
    {
      root: "Income",
      columns: [
        { key: "Paycheck", label: "Paycheck" },
        { key: "TaxRefund", label: "Tax Refund" },
        { key: "HandCash", label: "Hand Cash" },
        { key: "Cheque_Deposit", label: "Cheque Deposit" },
        { key: "Other_Income", label: "Other Income" },
      ],
    },
    {
      root: "Investments",
      columns: [
        { key: "Equity_Investments", label: "Equity Investments" },
        { key: "Savings", label: "Savings" },
      ],
    },
    {
      root: "House",
      columns: [
        { key: "House_Rent", label: "House Rent" },
        { key: "House_Utilities", label: "House Utilities" },
        { key: "House_Insurance", label: "House Insurance" },
        { key: "Edision", label: "Edision" },
        { key: "Laundry_Expenses", label: "Laundry Expenses" },
        { key: "Water", label: "Water" },
        { key: "Groceries", label: "Groceries" },
        { key: "Food_Expenses", label: "Food Expenses" },
      ],
    },
    {
      root: "Car",
      columns: [
        { key: "Car_Lease", label: "Car Lease" },
        { key: "Car_Insurance", label: "Car Insurance" },
        { key: "Car_Registration_Renewal", label: "Car Registration Renewal" },
        { key: "Gas_Expenses", label: "Gas Expenses" },
      ],
    },
    {
      root: "Network",
      columns: [
        { key: "Rebtel", label: "Rebtel" },
        { key: "Mobile_Bill", label: "Mobile Bill" },
        { key: "Internet", label: "Internet" },
      ],
    },
    {
      root: "Transfer",
      columns: [{ key: "Transfer", label: "Transfer" }],
    },
    {
      root: "Other",
      columns: [
        { key: "Abacus", label: "Abacus Fees" },
        { key: "Chess_Fees", label: "Chess Fees" },
        { key: "Cas_Withdrawal", label: "Cash Withdrawal" },
        { key: "Shopping", label: "Shopping" },
        { key: "Entertainment", label: "Entertainment" },
        { key: "Passport_Renewal", label: "Passport Renewal" },
        { key: "Grooming", label: "Grooming" },
        { key: "Medical_Expenses", label: "Medical Expenses" },
        { key: "Sports", label: "Sports" },
        { key: "School", label: "School" },
        { key: "HandLoan_Repayment", label: "HandLoan Repayment" },
        { key: "IncomeTax", label: "Income Tax" },
        { key: "OneTimePay", label: "One Time Pay" },
        { key: "Other_Expenses", label: "Other Expenses" },
      ],
    },
  ];

  // Remove total column - using group summary rows instead
  const columns = [...baseColumns, ...columnGroups.flatMap((g) => g.columns)];
  const INDcolumnGroups = [
    {
      root: "Income",
      columns: [
        { key: "Paycheck", label: "Paycheck" },
        { key: "USA_Salary", label: "USA Salary" },
        { key: "Residential_Rent", label: "Rent" },
        { key: "House_Advance", label: "Rent Deposit" },
        { key: "GeniusNexa_Income", label: "GN Income" },
        { key: "Renunumari_Income", label: "Renu" },
        { key: "Gpay_Cashback", label: "Gpay CB" },
        { key: "Savings_Interest", label: "Savings Int." },
        { key: "Dividend_Income", label: "Dividend" },
        { key: "Tax_Refund", label: "Tax Refund" },
        { key: "Handcash", label: "Hand Cash" },
        { key: "Other_Income", label: "Other" },
      ],
    },
    {
      root: "Investments",
      columns: [
        { key: "Mutual_Fund", label: "Mutual Fund" },
        { key: "Pension", label: "Pension" },
        { key: "Stock", label: "Stock" },
        { key: "ICICI_Life_Insurance", label: "ICICI Life" },
        { key: "LIC", label: "LIC" },
      ],
    },
    {
      root: "Debt",
      columns: [
        { key: "Chitfund", label: "Chit" },
        { key: "Personal_Loan", label: "Loan" },
        { key: "HandCash_Repayment", label: "Cash Repay" },
      ],
    },
    {
      root: "House",
      columns: [
        { key: "EBBills", label: "EB" },
        { key: "PropertyTax", label: "Prop Tax" },
      ],
    },
    {
      root: "Mobile",
      columns: [
        { key: "Mobile_Recharge", label: "Recharge" },
        { key: "BSNL_Bills", label: "BSNL" },
      ],
    },
    {
      root: "Family",
      columns: [
        { key: "Personal_Donations", label: "Gift" },
        { key: "Pooja_Expenses", label: "Pooja" },
        { key: "To_Prakish", label: "Prakish" },
        { key: "Diwali_Gift_To_Family", label: "Diwali Gift" },
        { key: "To_Amma", label: "Amma" },
      ],
    },
    {
      root: "Tuition Fees",
      columns: [
        { key: "Abacus", label: "Abacus Fees" },
        { key: "Chess_Fees", label: "Chess Fees" },
      ],
    },
    {
      root: "Other",
      columns: [
        { key: "ATM_Withdraw", label: "Cash W/D" },
        { key: "Cab_Expenses", label: "Cab" },
        { key: "Taxfiling_Expenses", label: "Tax Filing" },
        { key: "GeniusNexa_Bills", label: "GN Bills" },
        { key: "Credit_Card_Payment", label: "CC Payment" },
        { key: "Other_Expenses", label: "Other" },
        { key: "Billing_Errors", label: "Bill Errors" },
      ],
    },
  ];

  // Remove total column - using group summary rows instead
  const Indcolumns = [
    ...baseColumns,
    ...INDcolumnGroups.flatMap((g) => g.columns),
  ];
  // Month abbreviation helper
  const getMonthAbbr = (month) => {
    if (!month) return "";
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const m = Number(month);
    return months[m - 1] || month;
  };

  // Format currency
  const formatCurrency = (val) => {
    if (val === undefined || val === null || val === "") return "";
    const num = Number(val);
    if (isNaN(num)) return val;

    // If compact format is disabled, show exact figure
    if (!showCompactCurrency) {
      return `$${num.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }

    // Format in compact style with appropriate suffixes
    if (num >= 1000000) {
      // 1 million and above
      return `$${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      // 1 thousand and above
      return `$${(num / 1000).toFixed(0)}K`;
    } else if (num >= 100) {
      // 100 and above
      return `$${num.toFixed(0)}`;
    } else {
      return `$${num.toFixed(2)}`;
    }
  };

  const formatIndCurrency = (val) => {
    if (val === undefined || val === null || val === "") return "";
    const num = Number(val);
    if (isNaN(num)) return val;

    // If compact format is disabled, show exact figure
    if (!showCompactCurrency) {
      return num.toLocaleString("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    // Format in Indian style with appropriate suffixes
    if (num >= 10000000) {
      // 1 crore and above
      return `₹${(num / 10000000).toFixed(1)}Cr`;
    } else if (num >= 100000) {
      // 1 lakh and above
      return `₹${(num / 100000).toFixed(0)}L`;
    } else if (num >= 1000) {
      // 1 thousand and above
      return `₹${(num / 1000).toFixed(0)}K`;
    } else if (num >= 100) {
      // 100 and above
      return `₹${num.toFixed(0)}`;
    } else {
      return `₹${num.toFixed(2)}`;
    }
  };

  // Helper to sum numeric columns (skip Month/Year) - Original total row
  const getTotalRow = () => {
    if (summary.length === 0) return null;
    const total = {};
    columns.forEach((col) => {
      if (col.key === "month" || col.key === "year") {
        total[col.key] = "Total";
      } else {
        total[col.key] = summary.reduce((acc, row) => {
          const val = Number(row[col.key]);
          return acc + (isNaN(val) ? 0 : val);
        }, 0);
      }
    });
    return total;
  };

  const getIndTotalRow = () => {
    if (summary.length === 0) return null;
    const total = {};
    Indcolumns.forEach((col) => {
      if (col.key === "month" || col.key === "year") {
        total[col.key] = "Total";
      } else {
        total[col.key] = summary.reduce((acc, row) => {
          const val = Number(row[col.key]);
          return acc + (isNaN(val) ? 0 : val);
        }, 0);
      }
    });
    return total;
  };

  const totalRow = getTotalRow();
  const IndtotalRow = getIndTotalRow();

  // Calculate group totals for headers
  const getGroupTotal = (group, isIndia = false) => {
    if (summary.length === 0) return 0;
    return summary.reduce((sum, row) => {
      return (
        sum +
        group.columns.reduce(
          (colSum, col) => colSum + (Number(row[col.key]) || 0),
          0,
        )
      );
    }, 0);
  };

  // Filter summary data based on search query
  const filteredSummary = summary.filter((row) =>
    Object.values(row).some(
      (value) =>
        value &&
        value.toString().toLowerCase().includes(searchQuery.toLowerCase()),
    ),
  );

  // Get statistics for dashboard cards
  const getStatistics = () => {
    if (filteredSummary.length === 0) return {};

    if (selectedTab === "USA") {
      // USA Tab - New calculation logic
      const incomeColumns = [
        "Paycheck",
        "TaxRefund",
        "HandCash",
        "Cheque_Deposit",
        "Other_Income",
      ];
      const investmentColumns = ["Equity_Investments", "Savings", "Transfer"];
      const spendingColumns = [
        // House
        "House_Rent",
        "House_Utilities",
        "House_Insurance",
        "Edision",
        "Laundry_Expenses",
        "Water",
        "Groceries",
        "Food_Expenses",
        // Car
        "Car_Lease",
        "Car_Insurance",
        "Car_Registration_Renewal",
        "Gas_Expenses",
        // Network
        "Rebtel",
        "Mobile_Bill",
        "Internet",
        // Other
        "Abacus",
        "Chess_Fees",
        "Cas_Withdrawal",
        "Shopping",
        "Entertainment",
        "Passport_Renewal",
        "Grooming",
        "Medical_Expenses",
        "Sports",
        "School",
        "HandLoan_Repayment",
        "IncomeTax",
        "OneTimePay",
        "Other_Expenses",
      ];

      const totalIncome = filteredSummary.reduce((acc, row) => {
        return (
          acc +
          incomeColumns.reduce((sum, col) => sum + (Number(row[col]) || 0), 0)
        );
      }, 0);

      const totalInvestments = filteredSummary.reduce((acc, row) => {
        return (
          acc +
          investmentColumns.reduce(
            (sum, col) => sum + (Number(row[col]) || 0),
            0,
          )
        );
      }, 0);

      const totalSpending = filteredSummary.reduce((acc, row) => {
        return (
          acc +
          spendingColumns.reduce((sum, col) => sum + (Number(row[col]) || 0), 0)
        );
      }, 0);

      const totalSavings = totalIncome - (totalInvestments + totalSpending);
      const savingsPercentage =
        totalIncome > 0 ? (totalSavings / totalIncome) * 100 : 0;
      const investmentPercentage =
        totalIncome > 0 ? (totalInvestments / totalIncome) * 100 : 0;
      const spendingPercentage =
        totalIncome > 0 ? (totalSpending / totalIncome) * 100 : 0;
      const avgMonthlyIncome =
        filteredSummary.length > 0 ? totalIncome / filteredSummary.length : 0;

      return {
        totalIncome,
        totalInvestments,
        totalSpending,
        totalSavings,
        savingsPercentage,
        investmentPercentage,
        spendingPercentage,
        avgMonthlyIncome,
        monthsWithData: filteredSummary.length,
      };
    } else {
      // INDIA Tab - Updated to match USA format
      const incomeColumns = [
        "Paycheck",
        "USA_Salary",
        "Residential_Rent",
        "House_Advance",
        "GeniusNexa_Income",
        "Renunumari_Income",
        "Gpay_Cashback",
        "Savings_Interest",
        "Dividend_Income",
        "Tax_Refund",
        "Handcash",
        "Other_Income",
      ];

      const investmentColumns = [
        "Mutual_Fund",
        "Pension",
        "Stock",
        "ICICI_Life_Insurance",
        "LIC",
      ];

      const spendingColumns = [
        // Debt
        "Chitfund",
        "Personal_Loan",
        "HandCash_Repayment",
        // House
        "EBBills",
        "PropertyTax",
        // Mobile
        "Mobile_Recharge",
        "BSNL_Bills",
        // Family
        "Personal_Donations",
        "Pooja_Expenses",
        "To_Prakish",
        "Diwali_Gift_To_Family",
        "To_Amma",
        // Tuition Fees
        "Abacus",
        "Chess_Fees",
        // Other (excluding Billing_Errors as requested)
        "ATM_Withdraw",
        "Cab_Expenses",
        "Taxfiling_Expenses",
        "GeniusNexa_Bills",
        "Credit_Card_Payment",
        "Other_Expenses",
      ];

      const totalIncome = filteredSummary.reduce((acc, row) => {
        return (
          acc +
          incomeColumns.reduce((sum, col) => sum + (Number(row[col]) || 0), 0)
        );
      }, 0);

      const totalInvestments = filteredSummary.reduce((acc, row) => {
        return (
          acc +
          investmentColumns.reduce(
            (sum, col) => sum + (Number(row[col]) || 0),
            0,
          )
        );
      }, 0);

      const totalSpending = filteredSummary.reduce((acc, row) => {
        return (
          acc +
          spendingColumns.reduce((sum, col) => sum + (Number(row[col]) || 0), 0)
        );
      }, 0);

      const totalSavings = totalIncome - (totalInvestments + totalSpending);
      const savingsPercentage =
        totalIncome > 0 ? (totalSavings / totalIncome) * 100 : 0;
      const investmentPercentage =
        totalIncome > 0 ? (totalInvestments / totalIncome) * 100 : 0;
      const spendingPercentage =
        totalIncome > 0 ? (totalSpending / totalIncome) * 100 : 0;
      const avgMonthlyIncome =
        filteredSummary.length > 0 ? totalIncome / filteredSummary.length : 0;

      return {
        totalIncome,
        totalInvestments,
        totalSpending,
        totalSavings,
        savingsPercentage,
        investmentPercentage,
        spendingPercentage,
        avgMonthlyIncome,
        monthsWithData: filteredSummary.length,
      };
    }
  };

  const statistics = getStatistics();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {isAdminUser() ? (
        <div className="max-w-7xl mx-auto p-6">
          {/* Enhanced Tab Navigation */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 mb-6">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleGoBack}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <FontAwesomeIcon
                    icon={faArrowLeft}
                    className="text-gray-600"
                  />
                  <span className="text-gray-700 font-medium">Back</span>
                </button>
                <h1 className="text-2xl font-bold text-gray-900">
                  Financial Dashboard
                </h1>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 p-4">
              {[
                {
                  key: "USA",
                  label: "🇺🇸 USA Transactions",
                  icon: faDollarSign,
                },
                {
                  key: "INDIA",
                  label: "🇮🇳 India Transactions",
                  icon: faRupeeSign,
                },
                {
                  key: "Health Record",
                  label: "🏥 Health Records",
                  icon: faChartLine,
                },
                {
                  key: "Investment Details",
                  label: "📈 Investments",
                  icon: faChartLine,
                },
                {
                  key: "Goals Management",
                  label: "🎯 Goals",
                  icon: faBullseye,
                },
                { key: "Bank Records", label: "🏦 Bank Records", icon: faEye },
                {
                  key: "CreditCard Records",
                  label: "💳 Credit Cards",
                  icon: faEye,
                },
                { key: "Summary View", label: "📊 Summary", icon: faEye },
              ].map((tab) => (
                <button
                  key={tab.key}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                    selectedTab === tab.key
                      ? "bg-blue-600 text-white shadow-lg transform scale-105"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-900"
                  }`}
                  onClick={() => setSelectedTab(tab.key)}
                >
                  <FontAwesomeIcon icon={tab.icon} className="text-sm" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
          {/* USA Transactions Content */}
          {selectedTab === "USA" && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              {/* Controls Section */}
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
                <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-end">
                  <div className="min-w-[250px]">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
                      Select Years
                    </label>
                    <Select
                      options={yearOptions}
                      value={selectedYears}
                      onChange={setSelectedYears}
                      isMulti
                      isSearchable={false}
                      placeholder="Choose years to analyze"
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: 44,
                          border: "2px solid #e5e7eb",
                          borderRadius: "12px",
                          "&:hover": { borderColor: "#3b82f6" },
                        }),
                        menu: (provided) => ({ ...provided, zIndex: 9999 }),
                        multiValue: (provided) => ({
                          ...provided,
                          backgroundColor: "#dbeafe",
                          borderRadius: "8px",
                        }),
                        multiValueLabel: (provided) => ({
                          ...provided,
                          color: "#1e40af",
                          fontWeight: "500",
                        }),
                      }}
                    />
                  </div>
                  <button
                    className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 transform hover:scale-105 shadow-lg"
                    onClick={fetchSummary}
                    disabled={loading || selectedYears.length === 0}
                  >
                    <FontAwesomeIcon icon={faSearch} />
                    <span>{loading ? "Loading..." : "Fetch Summary"}</span>
                  </button>
                  <button
                    className={`flex items-center space-x-2 px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
                      showCompactCurrency
                        ? "bg-green-600 hover:bg-green-700 text-white"
                        : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                    }`}
                    onClick={() => setShowCompactCurrency(!showCompactCurrency)}
                    title={
                      showCompactCurrency
                        ? "Show exact amounts"
                        : "Show compact format"
                    }
                  >
                    <FontAwesomeIcon icon={faDollarSign} />
                    <span>{showCompactCurrency ? "$50K" : "$50,000.00"}</span>
                  </button>
                </div>

                {/* Search Filter */}
                {summary.length > 0 && (
                  <div className="relative">
                    <FontAwesomeIcon
                      icon={faSearch}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="text"
                      placeholder="Search transactions..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />
                  </div>
                )}
              </div>

              {/* Statistics Cards for USA */}
              {filteredSummary.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                  {selectedTab === "USA" ? (
                    <>
                      <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-blue-600 text-sm font-medium uppercase tracking-wider">
                              Income
                            </p>
                            <p className="text-2xl font-bold text-blue-900 mt-2">
                              {formatCurrency(statistics.totalIncome)}
                            </p>
                            <p className="text-sm text-blue-700 mt-1">
                              Avg: {formatCurrency(statistics.avgMonthlyIncome)}
                              /mo
                            </p>
                          </div>
                          <div className="bg-blue-200 p-3 rounded-xl">
                            <FontAwesomeIcon
                              icon={faDollarSign}
                              className="text-blue-700 text-xl"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="bg-gradient-to-r from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-green-600 text-sm font-medium uppercase tracking-wider">
                              Investments
                            </p>
                            <p className="text-2xl font-bold text-green-900 mt-2">
                              {formatCurrency(statistics.totalInvestments)}
                            </p>
                            <p className="text-sm text-green-700 mt-1">
                              {statistics.investmentPercentage?.toFixed(1)}% of
                              income
                            </p>
                          </div>
                          <div className="bg-green-200 p-3 rounded-xl">
                            <FontAwesomeIcon
                              icon={faChartLine}
                              className="text-green-700 text-xl"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="bg-gradient-to-r from-red-50 to-red-100 p-6 rounded-xl border border-red-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-red-600 text-sm font-medium uppercase tracking-wider">
                              Spending
                            </p>
                            <p className="text-2xl font-bold text-red-900 mt-2">
                              {formatCurrency(statistics.totalSpending)}
                            </p>
                            <p className="text-sm text-red-700 mt-1">
                              {statistics.spendingPercentage?.toFixed(1)}% of
                              income
                            </p>
                          </div>
                          <div className="bg-red-200 p-3 rounded-xl">
                            <FontAwesomeIcon
                              icon={faFileExport}
                              className="text-red-700 text-xl"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-purple-600 text-sm font-medium uppercase tracking-wider">
                              Unaccounted
                            </p>
                            <p className="text-2xl font-bold text-purple-900 mt-2">
                              {formatCurrency(statistics.totalSavings)}
                            </p>
                            <p className="text-sm text-purple-700 mt-1">
                              {statistics.savingsPercentage?.toFixed(1)}% of
                              income
                            </p>
                          </div>
                          <div className="bg-purple-200 p-3 rounded-xl">
                            <FontAwesomeIcon
                              icon={faRupeeSign}
                              className="text-purple-700 text-xl"
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-blue-600 text-sm font-medium uppercase tracking-wider">
                              Total Income
                            </p>
                            <p className="text-2xl font-bold text-blue-900 mt-2">
                              {formatCurrency(statistics.totalIncome)}
                            </p>
                          </div>
                          <div className="bg-blue-200 p-3 rounded-xl">
                            <FontAwesomeIcon
                              icon={faDollarSign}
                              className="text-blue-700 text-xl"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="bg-gradient-to-r from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-green-600 text-sm font-medium uppercase tracking-wider">
                              Avg Monthly
                            </p>
                            <p className="text-2xl font-bold text-green-900 mt-2">
                              {formatCurrency(statistics.avgMonthlyIncome)}
                            </p>
                          </div>
                          <div className="bg-green-200 p-3 rounded-xl">
                            <FontAwesomeIcon
                              icon={faChartLine}
                              className="text-green-700 text-xl"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-purple-600 text-sm font-medium uppercase tracking-wider">
                              Data Points
                            </p>
                            <p className="text-2xl font-bold text-purple-900 mt-2">
                              {statistics.monthsWithData}
                            </p>
                          </div>
                          <div className="bg-purple-200 p-3 rounded-xl">
                            <FontAwesomeIcon
                              icon={faCalendarAlt}
                              className="text-purple-700 text-xl"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-6 rounded-xl border border-orange-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-orange-600 text-sm font-medium uppercase tracking-wider">
                              Years Selected
                            </p>
                            <p className="text-2xl font-bold text-orange-900 mt-2">
                              {statistics.yearsSelected}
                            </p>
                          </div>
                          <div className="bg-orange-200 p-3 rounded-xl">
                            <FontAwesomeIcon
                              icon={faFilter}
                              className="text-orange-700 text-xl"
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Enhanced Investment Portfolio Section - USA */}
              {filteredSummary.length > 0 && selectedTab === "USA" && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-900 flex items-center">
                      <FontAwesomeIcon
                        icon={faChartLine}
                        className="mr-2 text-blue-600"
                      />
                      Investment Portfolio Dashboard
                    </h2>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">
                        Total Portfolio Value
                      </p>
                      <p className="text-2xl font-bold text-green-600">
                        {formatCurrency(statistics.totalInvestments)}
                      </p>
                    </div>
                  </div>

                  {/* Quick Portfolio Stats */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    {[
                      {
                        key: "Equity_Investments",
                        label: "Equity",
                        icon: "📈",
                        color: "blue",
                      },
                      {
                        key: "Savings",
                        label: "Savings",
                        icon: "💰",
                        color: "green",
                      },
                      {
                        key: "Transfer",
                        label: "Transfer",
                        icon: "🔄",
                        color: "purple",
                      },
                    ].map((investment) => {
                      const amount = filteredSummary.reduce(
                        (sum, row) => sum + (Number(row[investment.key]) || 0),
                        0,
                      );
                      const percentage =
                        statistics.totalInvestments > 0
                          ? (amount / statistics.totalInvestments) * 100
                          : 0;
                      const avgMonthly =
                        filteredSummary.length > 0
                          ? amount / filteredSummary.length
                          : 0;
                      return (
                        <div
                          key={investment.key}
                          className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-xl border border-gray-200 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-2xl">{investment.icon}</span>
                            <span className="text-xs font-semibold px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                              {percentage.toFixed(1)}%
                            </span>
                          </div>
                          <h4 className="font-semibold text-gray-800 text-sm mb-1">
                            {investment.label}
                          </h4>
                          <p className="text-lg font-bold text-gray-900 mb-1">
                            {formatCurrency(amount)}
                          </p>
                          <p className="text-xs text-gray-600">
                            Avg: {formatCurrency(avgMonthly)}/mo
                          </p>
                        </div>
                      );
                    })}
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Investment Allocation Chart */}
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">
                        Portfolio Allocation
                      </h3>
                      <div className="space-y-3">
                        {[
                          {
                            key: "Equity_Investments",
                            label: "Equity",
                            color: "bg-blue-500",
                          },
                          {
                            key: "Savings",
                            label: "Savings",
                            color: "bg-green-500",
                          },
                          {
                            key: "Transfer",
                            label: "Transfer",
                            color: "bg-purple-500",
                          },
                        ].map((investment) => {
                          const total = statistics.totalInvestments;
                          const amount = filteredSummary.reduce(
                            (sum, row) =>
                              sum + (Number(row[investment.key]) || 0),
                            0,
                          );
                          const percentage =
                            total > 0 ? (amount / total) * 100 : 0;
                          return (
                            <div
                              key={investment.key}
                              className="flex items-center justify-between"
                            >
                              <div className="flex items-center space-x-3">
                                <div
                                  className={`w-4 h-4 rounded ${investment.color}`}
                                ></div>
                                <span className="text-sm font-medium text-gray-700">
                                  {investment.label}
                                </span>
                              </div>
                              <div className="flex items-center space-x-3">
                                <div className="w-32 bg-gray-200 rounded-full h-2">
                                  <div
                                    className={`h-2 rounded-full ${investment.color}`}
                                    style={{ width: `${percentage}%` }}
                                  ></div>
                                </div>
                                <span className="text-sm font-bold text-gray-800 w-16 text-right">
                                  {percentage.toFixed(1)}%
                                </span>
                                <span className="text-sm text-gray-600 w-20 text-right">
                                  {formatCurrency(amount)}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Enhanced Investment Performance */}
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <span className="text-xl mr-2">📊</span>
                        Performance & Trends
                      </h3>
                      <div className="space-y-3">
                        {filteredSummary.slice(-6).map((row, index) => {
                          const monthInvestment =
                            (Number(row["Equity_Investments"]) || 0) +
                            (Number(row["Savings"]) || 0) +
                            (Number(row["Transfer"]) || 0);

                          // Calculate growth rate
                          const prevMonthData =
                            filteredSummary[
                              filteredSummary.length - 6 + index - 1
                            ];
                          const prevMonthInvestment =
                            index > 0 && prevMonthData
                              ? (Number(prevMonthData["Equity_Investments"]) ||
                                  0) +
                                (Number(prevMonthData["Savings"]) || 0) +
                                (Number(prevMonthData["Transfer"]) || 0)
                              : monthInvestment;

                          const growthRate =
                            index > 0 && prevMonthInvestment > 0
                              ? ((monthInvestment - prevMonthInvestment) /
                                  prevMonthInvestment) *
                                100
                              : 0;

                          const maxInvestment = Math.max(
                            ...filteredSummary.map(
                              (r) =>
                                (Number(r["Equity_Investments"]) || 0) +
                                (Number(r["Savings"]) || 0) +
                                (Number(r["Transfer"]) || 0),
                            ),
                          );
                          const barWidth =
                            maxInvestment > 0
                              ? (monthInvestment / maxInvestment) * 100
                              : 0;
                          return (
                            <div
                              key={index}
                              className="flex items-center space-x-3"
                            >
                              <div className="w-20">
                                <span className="text-xs font-medium text-gray-600">
                                  {row.month}/{row.year}
                                </span>
                                {index > 0 && (
                                  <div
                                    className={`text-xs font-semibold ${
                                      growthRate >= 0
                                        ? "text-green-600"
                                        : "text-red-600"
                                    }`}
                                  >
                                    {growthRate >= 0 ? "↗️" : "↘️"}{" "}
                                    {Math.abs(growthRate).toFixed(1)}%
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 bg-gray-200 rounded-full h-6">
                                <div
                                  className="bg-gradient-to-r from-emerald-400 to-emerald-600 h-6 rounded-full flex items-center justify-between px-2"
                                  style={{ width: `${barWidth}%` }}
                                >
                                  {barWidth > 25 && (
                                    <span className="text-xs text-white font-semibold">
                                      {formatCurrency(monthInvestment)}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {barWidth <= 25 && (
                                <span className="text-xs text-gray-600 w-24 text-right">
                                  {formatCurrency(monthInvestment)}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Portfolio Summary Stats */}
                      <div className="mt-6 pt-4 border-t border-emerald-200">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-white p-3 rounded-lg text-center">
                            <p className="text-xs text-gray-600 mb-1">
                              Best Month
                            </p>
                            <p className="text-lg font-bold text-green-600">
                              {formatCurrency(
                                Math.max(
                                  ...filteredSummary.map(
                                    (r) =>
                                      (Number(r["Equity_Investments"]) || 0) +
                                      (Number(r["Savings"]) || 0) +
                                      (Number(r["Transfer"]) || 0),
                                  ),
                                ),
                              )}
                            </p>
                          </div>
                          <div className="bg-white p-3 rounded-lg text-center">
                            <p className="text-xs text-gray-600 mb-1">
                              Monthly Avg
                            </p>
                            <p className="text-lg font-bold text-blue-600">
                              {formatCurrency(
                                statistics.totalInvestments /
                                  (filteredSummary.length || 1),
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {(
                searchQuery
                  ? filteredSummary.length === 0
                  : summary.length === 0
              ) ? (
                <div className="text-center py-12">
                  <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                    <FontAwesomeIcon
                      icon={faChartLine}
                      className="text-3xl text-gray-400"
                    />
                  </div>
                  <p className="text-gray-500 text-lg">
                    {searchQuery
                      ? `No results found for "${searchQuery}"`
                      : "No transaction data found"}
                  </p>
                  <p className="text-gray-400">
                    {searchQuery
                      ? "Try a different search term or clear the search"
                      : 'Select years and click "Fetch Summary" to view your financial data'}
                  </p>
                </div>
              ) : (
                <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl border-2 border-gray-200 overflow-hidden">
                  <div
                    className="overflow-auto"
                    style={{
                      height: "calc(100vh - 500px)",
                      minHeight: "400px",
                      maxHeight: "600px",
                      boxShadow: "inset 0 4px 20px rgba(59,130,246,0.1)",
                    }}
                  >
                    <table
                      className="w-full text-sm"
                      style={{
                        minWidth: "1200px",
                        borderCollapse: "separate",
                        borderSpacing: 0,
                      }}
                    >
                      <thead>
                        <tr className="bg-gradient-to-r from-blue-600 to-blue-700 text-white sticky top-0 z-30">
                          <th
                            className="border border-blue-500 px-4 py-3 text-center font-bold tracking-wide sticky left-0 z-40 bg-blue-600"
                            style={{
                              width: "80px",
                              boxShadow: "2px 0 12px -2px rgba(59,130,246,0.3)",
                            }}
                          >
                            Year
                          </th>
                          <th
                            className="border border-blue-500 px-4 py-3 text-center font-bold tracking-wide sticky left-[80px] z-40 bg-blue-600"
                            style={{
                              width: "80px",
                              boxShadow: "2px 0 12px -2px rgba(59,130,246,0.3)",
                            }}
                          >
                            Month
                          </th>
                          {columnGroups.map((group, i) => {
                            const groupTotal = getGroupTotal(group);
                            const displayTotal =
                              groupTotal > 0
                                ? ` (${formatCurrency(groupTotal)})`
                                : "";
                            return (
                              <th
                                key={group.root}
                                colSpan={group.columns.length}
                                className={`border border-blue-500 px-4 py-3 text-center font-bold tracking-wide bg-blue-600 ${
                                  i >= 0 ? "border-l-4 border-blue-400" : ""
                                }`}
                              >
                                {group.root === "Income"
                                  ? `💰 INCOME${displayTotal}`
                                  : group.root === "Expenses"
                                    ? `💸 EXPENSES${displayTotal}`
                                    : group.root === "Investments"
                                      ? `📈 INVESTMENTS${displayTotal}`
                                      : group.root === "House"
                                        ? `🏠 HOUSE${displayTotal}`
                                        : group.root === "Car"
                                          ? `🚗 CAR${displayTotal}`
                                          : group.root === "Network"
                                            ? `📡 NETWORK${displayTotal}`
                                            : group.root === "Transfer"
                                              ? `💸 TRANSFER${displayTotal}`
                                              : `${group.root}${displayTotal}`}
                              </th>
                            );
                          })}
                        </tr>
                        <tr className="bg-gradient-to-r from-blue-50 to-blue-100 sticky top-[52px] z-30">
                          {columns.map((col, idx) => (
                            <th
                              key={col.key}
                              className={`border border-gray-300 px-3 py-2 text-left whitespace-nowrap font-semibold text-gray-700 ${
                                idx === 0
                                  ? "sticky left-0 z-40 bg-blue-50"
                                  : idx === 1
                                    ? "sticky left-[80px] z-40 bg-blue-50"
                                    : ""
                              }`}
                              style={{
                                width: idx < 2 ? "80px" : "100px",
                                boxShadow:
                                  idx < 2
                                    ? "2px 0 8px -2px rgba(59,130,246,0.15)"
                                    : undefined,
                              }}
                            >
                              {col.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSummary.map((row, idx) => (
                          <tr
                            key={idx}
                            className={
                              idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                            }
                          >
                            {columns.map((col, colIdx) => {
                              const isFigure = colIdx >= 2;
                              let value,
                                diff = null,
                                arrow = null,
                                diffColor = "";
                              if (col.key === "month") {
                                value = getMonthAbbr(row["month"]);
                              } else if (col.key === "year") {
                                value = row["year"];
                              } else {
                                value = formatCurrency(row[col.key]);
                                // Month-over-month comparison
                                if (idx > 0) {
                                  const prevRow = summary[idx - 1];
                                  const currVal = Number(row[col.key]) || 0;
                                  const prevVal = Number(prevRow[col.key]) || 0;
                                  diff = currVal - prevVal;
                                  if (diff !== 0) {
                                    // Income/Investment columns: up arrow if diff > 0, down if < 0
                                    // Expense columns: up arrow if diff < 0, down if > 0
                                    const incomeKeys = [
                                      "Paycheck",
                                      "TaxRefund",
                                      "HandCash",
                                      "Cheque_Deposit",
                                      "Other_Income",
                                      "Equity_Investments",
                                      "Savings",
                                    ];
                                    const expenseKeys = [
                                      "House_Rent",
                                      "House_Utilities",
                                      "House_Insurance",
                                      "Edision",
                                      "Laundry_Expenses",
                                      "Water",
                                      "Groceries",
                                      "Food_Expenses",
                                      "Car_Lease",
                                      "Car_Insurance",
                                      "Car_Registration_Renewal",
                                      "Gas_Expenses",
                                      "Rebtel",
                                      "Mobile_Bill",
                                      "Internet",
                                      "Cas_Withdrawal",
                                      "Transfer",
                                      "Shopping",
                                      "Entertainment",
                                      "Passport_Renewal",
                                      "Grooming",
                                      "Medical_Expenses",
                                      "Sports",
                                      "School",
                                      "HandLoan_Repayment",
                                      "IncomeTax",
                                      "OneTimePay",
                                      "Other_Expenses",
                                    ];
                                    if (
                                      incomeKeys.includes(col.key) ||
                                      col.key === "year" ||
                                      col.key === "month"
                                    ) {
                                      arrow = diff > 0 ? "▲" : "▼";
                                      diffColor =
                                        diff > 0
                                          ? "text-green-600"
                                          : "text-red-600";
                                    } else if (expenseKeys.includes(col.key)) {
                                      arrow = diff < 0 ? "▲" : "▼";
                                      diffColor =
                                        diff < 0
                                          ? "text-green-600"
                                          : "text-red-600";
                                    }
                                  }
                                }
                              }
                              return (
                                <td
                                  key={col.key}
                                  className={`border border-gray-200 px-2 py-1 whitespace-nowrap text-${
                                    colIdx < 2
                                      ? "left font-semibold"
                                      : "right text-blue-900 font-bold"
                                  } ${
                                    colIdx === 0
                                      ? "sticky left-0 bg-white z-20"
                                      : colIdx === 1
                                        ? "sticky left-[80px] bg-white z-20"
                                        : ""
                                  } ${
                                    isFigure && value !== "$0.00"
                                      ? "cursor-pointer hover:bg-blue-50"
                                      : ""
                                  }`}
                                  style={{
                                    minWidth: 80,
                                    boxShadow:
                                      colIdx < 2
                                        ? "2px 0 8px -2px rgba(59,130,246,0.10)"
                                        : undefined,
                                  }}
                                  onClick={
                                    isFigure && value !== "$0.00"
                                      ? () =>
                                          handleCellClick(
                                            col.key,
                                            row["year"],
                                            row["month"],
                                          )
                                      : undefined
                                  }
                                  title={
                                    isFigure && value !== "$0.00"
                                      ? `View details for ${col.label}`
                                      : undefined
                                  }
                                >
                                  {value}
                                  {diff !== null && isFigure && (
                                    <span
                                      className={`ml-1 text-xs ${diffColor}`}
                                      title={`Change: ${diff}`}
                                    >
                                      {" "}
                                      {arrow} {formatCurrency(diff)}
                                    </span>
                                  )}
                                </td>
                              );
                            })}
                            {/* Modal for details */}
                            {modalOpen && (
                              <div
                                className="fixed inset-0 z-50 flex items-center justify-center"
                                style={{ background: "rgba(245,247,250,0.85)" }}
                              >
                                <div className="bg-white rounded-lg shadow-xl w-[1100px] p-0 border border-gray-300 relative">
                                  <button
                                    className="absolute top-3 right-4 text-gray-500 hover:text-gray-700 text-2xl font-bold"
                                    onClick={() => setModalOpen(false)}
                                    aria-label="Close"
                                    style={{ lineHeight: 1 }}
                                  >
                                    &times;
                                  </button>
                                  <div className="px-6 pt-5 pb-2 border-b bg-gray-50 rounded-t-lg">
                                    <h3 className="text-lg font-bold text-blue-700">
                                      {modalTitle}
                                    </h3>
                                  </div>
                                  <div className="px-6 pb-6 pt-2">
                                    {modalLoading ? (
                                      <div className="text-center py-8 text-blue-600 font-semibold">
                                        Loading...
                                      </div>
                                    ) : modalData.length === 0 ? (
                                      <div className="text-center py-8 text-gray-500">
                                        No records found.
                                      </div>
                                    ) : (
                                      <div
                                        style={{
                                          maxHeight: "340px",
                                          overflowY: "auto",
                                        }}
                                      >
                                        <table
                                          className="w-full text-sm border border-gray-300 rounded-lg"
                                          style={{ tableLayout: "fixed" }}
                                        >
                                          <thead>
                                            <tr className="bg-gray-100">
                                              <th className="border px-3 py-2 text-left w-[110px]">
                                                Date
                                              </th>
                                              <th className="border px-3 py-2 text-left w-[110px]">
                                                Transaction
                                              </th>
                                              <th className="border px-3 py-2 text-left w-[220px]">
                                                Description
                                              </th>
                                              <th className="border px-3 py-2 text-right w-[110px]">
                                                Amount
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {modalData.map((item, i) => (
                                              <tr
                                                key={i}
                                                className={
                                                  i % 2 === 0
                                                    ? "bg-white"
                                                    : "bg-gray-50"
                                                }
                                              >
                                                <td className="border px-3 py-2 text-left font-mono text-xs">
                                                  {item.transaction_date ||
                                                    item.TransactionDate ||
                                                    item.date ||
                                                    "-"}
                                                </td>
                                                <td className="border px-3 py-2 text-left">
                                                  {item.transaction_type ||
                                                    item.type ||
                                                    "-"}
                                                </td>
                                                <td className="border px-3 py-2 text-left">
                                                  {item.description ||
                                                    item.Description ||
                                                    "-"}
                                                </td>
                                                <td className="border px-3 py-2 text-right font-bold text-blue-900">
                                                  {formatCurrency(
                                                    item.amount || item.Amount,
                                                  )}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </tr>
                        ))}
                        {/* Original Total Row */}
                        {totalRow && (
                          <tr className="bg-yellow-100 font-bold sticky bottom-0 z-40">
                            {columns.map((col, colIdx) => (
                              <td
                                key={col.key}
                                className={`border border-gray-200 px-2 py-1 text-${
                                  colIdx < 2
                                    ? "left"
                                    : "right text-blue-900 font-bold"
                                } ${
                                  colIdx === 0
                                    ? "sticky left-0 z-40 bg-yellow-100"
                                    : colIdx === 1
                                      ? "sticky left-[80px] z-40 bg-yellow-100"
                                      : ""
                                }`}
                                style={{
                                  minWidth: 80,
                                  boxShadow:
                                    colIdx < 2
                                      ? "2px 0 8px -2px rgba(59,130,246,0.10)"
                                      : undefined,
                                }}
                              >
                                {colIdx === 0
                                  ? "Total"
                                  : colIdx === 1
                                    ? ""
                                    : formatCurrency(totalRow[col.key])}
                              </td>
                            ))}
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* India Transactions Content */}
          {selectedTab === "INDIA" && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              {/* Controls Section */}
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
                <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-end">
                  <div className="min-w-[250px]">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
                      Select Years
                    </label>
                    <Select
                      options={yearOptions}
                      value={selectedYears}
                      onChange={setSelectedYears}
                      isMulti
                      isSearchable={false}
                      placeholder="Choose years to analyze"
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: 44,
                          border: "2px solid #e5e7eb",
                          borderRadius: "12px",
                          "&:hover": { borderColor: "#3b82f6" },
                        }),
                        menu: (provided) => ({ ...provided, zIndex: 9999 }),
                        multiValue: (provided) => ({
                          ...provided,
                          backgroundColor: "#dbeafe",
                          borderRadius: "8px",
                        }),
                        multiValueLabel: (provided) => ({
                          ...provided,
                          color: "#1e40af",
                          fontWeight: "500",
                        }),
                      }}
                    />
                  </div>
                  <button
                    className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 transform hover:scale-105 shadow-lg"
                    onClick={fetchSummary}
                    disabled={loading || selectedYears.length === 0}
                  >
                    <FontAwesomeIcon icon={faSearch} />
                    <span>{loading ? "Loading..." : "Fetch Summary"}</span>
                  </button>
                  <button
                    className={`flex items-center space-x-2 px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
                      showCompactCurrency
                        ? "bg-orange-600 hover:bg-orange-700 text-white"
                        : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                    }`}
                    onClick={() => setShowCompactCurrency(!showCompactCurrency)}
                    title={
                      showCompactCurrency
                        ? "Show exact amounts"
                        : "Show compact format"
                    }
                  >
                    <FontAwesomeIcon icon={faRupeeSign} />
                    <span>{showCompactCurrency ? "39L" : "₹39,60,651"}</span>
                  </button>
                </div>

                {/* Search Filter */}
                {summary.length > 0 && (
                  <div className="relative">
                    <FontAwesomeIcon
                      icon={faSearch}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="text"
                      placeholder="Search transactions..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />
                  </div>
                )}
              </div>

              {/* Statistics Cards for INDIA */}
              {filteredSummary.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                  <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-600 text-sm font-medium uppercase tracking-wider">
                          Income
                        </p>
                        <p className="text-2xl font-bold text-blue-900 mt-2">
                          {formatIndCurrency(statistics.totalIncome)}
                        </p>
                        <p className="text-sm text-blue-700 mt-1">
                          Avg: {formatIndCurrency(statistics.avgMonthlyIncome)}
                          /mo
                        </p>
                      </div>
                      <div className="bg-blue-200 p-3 rounded-xl">
                        <FontAwesomeIcon
                          icon={faRupeeSign}
                          className="text-blue-700 text-xl"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-600 text-sm font-medium uppercase tracking-wider">
                          Investments
                        </p>
                        <p className="text-2xl font-bold text-green-900 mt-2">
                          {formatIndCurrency(statistics.totalInvestments)}
                        </p>
                        <p className="text-sm text-green-700 mt-1">
                          {statistics.investmentPercentage?.toFixed(1)}% of
                          income
                        </p>
                      </div>
                      <div className="bg-green-200 p-3 rounded-xl">
                        <FontAwesomeIcon
                          icon={faChartLine}
                          className="text-green-700 text-xl"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-red-50 to-red-100 p-6 rounded-xl border border-red-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-red-600 text-sm font-medium uppercase tracking-wider">
                          Spending
                        </p>
                        <p className="text-2xl font-bold text-red-900 mt-2">
                          {formatIndCurrency(statistics.totalSpending)}
                        </p>
                        <p className="text-sm text-red-700 mt-1">
                          {statistics.spendingPercentage?.toFixed(1)}% of income
                        </p>
                      </div>
                      <div className="bg-red-200 p-3 rounded-xl">
                        <FontAwesomeIcon
                          icon={faFileExport}
                          className="text-red-700 text-xl"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-600 text-sm font-medium uppercase tracking-wider">
                          Unaccounted
                        </p>
                        <p className="text-2xl font-bold text-purple-900 mt-2">
                          {formatIndCurrency(statistics.totalSavings)}
                        </p>
                        <p className="text-sm text-purple-700 mt-1">
                          {statistics.savingsPercentage?.toFixed(1)}% of income
                        </p>
                      </div>
                      <div className="bg-purple-200 p-3 rounded-xl">
                        <FontAwesomeIcon
                          icon={faRupeeSign}
                          className="text-purple-700 text-xl"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Enhanced Investment Portfolio Section - INDIA */}
              {filteredSummary.length > 0 && selectedTab === "INDIA" && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-900 flex items-center">
                      <FontAwesomeIcon
                        icon={faChartLine}
                        className="mr-2 text-blue-600"
                      />
                      Investment Portfolio Dashboard
                    </h2>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">
                        Total Portfolio Value
                      </p>
                      <p className="text-2xl font-bold text-green-600">
                        {formatIndCurrency(statistics.totalInvestments)}
                      </p>
                    </div>
                  </div>

                  {/* Quick Portfolio Stats for INDIA */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                    {[
                      {
                        key: "Mutual_Fund",
                        label: "Mutual Fund",
                        icon: "🏦",
                        color: "blue",
                      },
                      {
                        key: "Pension",
                        label: "Pension",
                        icon: "👴",
                        color: "green",
                      },
                      {
                        key: "Stock",
                        label: "Stock",
                        icon: "📈",
                        color: "red",
                      },
                      {
                        key: "ICICI_Life_Insurance",
                        label: "ICICI Life",
                        icon: "🛡️",
                        color: "purple",
                      },
                      { key: "LIC", label: "LIC", icon: "🏛️", color: "orange" },
                    ].map((investment) => {
                      const amount = filteredSummary.reduce(
                        (sum, row) => sum + (Number(row[investment.key]) || 0),
                        0,
                      );
                      const percentage =
                        statistics.totalInvestments > 0
                          ? (amount / statistics.totalInvestments) * 100
                          : 0;
                      return (
                        <div
                          key={investment.key}
                          className="bg-gradient-to-r from-gray-50 to-gray-100 p-3 rounded-xl border border-gray-200 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-lg">{investment.icon}</span>
                            <span className="text-xs font-semibold px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded-full">
                              {percentage.toFixed(1)}%
                            </span>
                          </div>
                          <h4 className="font-semibold text-gray-800 text-xs mb-1">
                            {investment.label}
                          </h4>
                          <p className="text-sm font-bold text-gray-900">
                            {formatIndCurrency(amount)}
                          </p>
                        </div>
                      );
                    })}
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Investment Allocation Chart */}
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">
                        Portfolio Allocation
                      </h3>
                      <div className="space-y-3">
                        {[
                          {
                            key: "Mutual_Fund",
                            label: "Mutual Fund",
                            color: "bg-blue-500",
                          },
                          {
                            key: "Pension",
                            label: "Pension",
                            color: "bg-green-500",
                          },
                          { key: "Stock", label: "Stock", color: "bg-red-500" },
                          {
                            key: "ICICI_Life_Insurance",
                            label: "ICICI Life",
                            color: "bg-purple-500",
                          },
                          { key: "LIC", label: "LIC", color: "bg-orange-500" },
                        ].map((investment) => {
                          const total = statistics.totalInvestments;
                          const amount = filteredSummary.reduce(
                            (sum, row) =>
                              sum + (Number(row[investment.key]) || 0),
                            0,
                          );
                          const percentage =
                            total > 0 ? (amount / total) * 100 : 0;
                          return (
                            <div
                              key={investment.key}
                              className="flex items-center justify-between"
                            >
                              <div className="flex items-center space-x-3">
                                <div
                                  className={`w-4 h-4 rounded ${investment.color}`}
                                ></div>
                                <span className="text-sm font-medium text-gray-700">
                                  {investment.label}
                                </span>
                              </div>
                              <div className="flex items-center space-x-3">
                                <div className="w-32 bg-gray-200 rounded-full h-2">
                                  <div
                                    className={`h-2 rounded-full ${investment.color}`}
                                    style={{ width: `${percentage}%` }}
                                  ></div>
                                </div>
                                <span className="text-sm font-bold text-gray-800 w-16 text-right">
                                  {percentage.toFixed(1)}%
                                </span>
                                <span className="text-sm text-gray-600 w-20 text-right">
                                  {formatIndCurrency(amount)}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Investment Trends */}
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">
                        Monthly Investment Trends
                      </h3>
                      <div className="space-y-4">
                        {filteredSummary.slice(-6).map((row, index) => {
                          const monthInvestment =
                            (Number(row["Mutual_Fund"]) || 0) +
                            (Number(row["Pension"]) || 0) +
                            (Number(row["Stock"]) || 0) +
                            (Number(row["ICICI_Life_Insurance"]) || 0) +
                            (Number(row["LIC"]) || 0);
                          const maxInvestment = Math.max(
                            ...filteredSummary.map(
                              (r) =>
                                (Number(r["Mutual_Fund"]) || 0) +
                                (Number(r["Pension"]) || 0) +
                                (Number(r["Stock"]) || 0) +
                                (Number(r["ICICI_Life_Insurance"]) || 0) +
                                (Number(r["LIC"]) || 0),
                            ),
                          );
                          const barWidth =
                            maxInvestment > 0
                              ? (monthInvestment / maxInvestment) * 100
                              : 0;
                          return (
                            <div
                              key={index}
                              className="flex items-center space-x-3"
                            >
                              <span className="text-xs font-medium text-gray-600 w-16">
                                {row.month}/{row.year}
                              </span>
                              <div className="flex-1 bg-gray-200 rounded-full h-4">
                                <div
                                  className="bg-gradient-to-r from-green-400 to-green-600 h-4 rounded-full flex items-center justify-end pr-2"
                                  style={{ width: `${barWidth}%` }}
                                >
                                  {barWidth > 20 && (
                                    <span className="text-xs text-white font-semibold">
                                      {formatIndCurrency(monthInvestment)}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {barWidth <= 20 && (
                                <span className="text-xs text-gray-600 w-20">
                                  {formatIndCurrency(monthInvestment)}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {(
                searchQuery
                  ? filteredSummary.length === 0
                  : summary.length === 0
              ) ? (
                <div className="text-center py-12">
                  <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                    <FontAwesomeIcon
                      icon={faChartLine}
                      className="text-3xl text-gray-400"
                    />
                  </div>
                  <p className="text-gray-500 text-lg">
                    {searchQuery
                      ? `No results found for "${searchQuery}"`
                      : "No transaction data found"}
                  </p>
                  <p className="text-gray-400">
                    {searchQuery
                      ? "Try a different search term or clear the search"
                      : 'Select years and click "Fetch Summary" to view your financial data'}
                  </p>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-gray-300 overflow-hidden">
                  <div
                    className="overflow-auto"
                    style={{
                      height: "calc(100vh - 300px)",
                      minHeight: "400px",
                      maxHeight: "600px",
                      boxShadow: "inset 0 2px 8px rgba(59,130,246,0.08)",
                    }}
                  >
                    <table
                      className="w-full text-sm"
                      style={{
                        minWidth: "1200px",
                        borderCollapse: "separate",
                        borderSpacing: 0,
                      }}
                    >
                      {" "}
                      <thead>
                        <tr className="bg-blue-100 sticky top-0 z-30">
                          <th
                            className="border border-gray-200 px-2 py-2 text-center font-semibold text-blue-700 bg-blue-50 sticky left-0 z-30"
                            style={{
                              width: "80px",
                              boxShadow: "2px 0 8px -2px rgba(59,130,246,0.10)",
                            }}
                          >
                            Year
                          </th>
                          <th
                            className="border border-gray-200 px-2 py-2 text-center font-semibold text-blue-700 bg-blue-50 sticky left-[80px] z-30"
                            style={{
                              width: "80px",
                              boxShadow: "2px 0 8px -2px rgba(59,130,246,0.10)",
                            }}
                          >
                            Month
                          </th>
                          {INDcolumnGroups.map((group, i) => {
                            const groupTotal = getGroupTotal(group, true);
                            const displayTotal =
                              groupTotal > 0
                                ? ` (${formatIndCurrency(groupTotal)})`
                                : "";
                            return (
                              <th
                                key={group.root}
                                colSpan={group.columns.length}
                                className={`border border-gray-200 px-2 py-2 text-center font-semibold text-blue-700 bg-blue-50 ${
                                  i >= 0 ? "border-l-4 border-gray-300" : ""
                                }`}
                              >
                                {`${group.root}${displayTotal}`}
                              </th>
                            );
                          })}
                        </tr>
                        <tr className="bg-gray-100 sticky top-[40px] z-20">
                          {Indcolumns.map((col, idx) => (
                            <th
                              key={col.key}
                              className={`border border-gray-200 px-2 py-1 text-left whitespace-nowrap font-medium ${
                                idx === 0
                                  ? "sticky left-0 z-30 bg-blue-50"
                                  : idx === 1
                                    ? "sticky left-[80px] z-30 bg-blue-50"
                                    : ""
                              }`}
                              style={{
                                width: idx < 2 ? "80px" : "90px",
                                boxShadow:
                                  idx < 2
                                    ? "2px 0 8px -2px rgba(59,130,246,0.10)"
                                    : undefined,
                              }}
                            >
                              {col.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSummary.map((row, idx) => (
                          <tr
                            key={idx}
                            className={
                              idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                            }
                          >
                            {Indcolumns.map((col, colIdx) => {
                              const isFigure = colIdx >= 2;
                              let value,
                                diff = null,
                                arrow = null,
                                diffColor = "";
                              if (col.key === "month") {
                                value = getMonthAbbr(row["month"]);
                              } else if (col.key === "year") {
                                value = row["year"];
                              } else {
                                value = formatIndCurrency(row[col.key]);
                                // Month-over-month comparison
                                if (idx > 0) {
                                  const prevRow = summary[idx - 1];
                                  const currVal = Number(row[col.key]) || 0;
                                  const prevVal = Number(prevRow[col.key]) || 0;
                                  diff = currVal - prevVal;
                                  if (diff !== 0) {
                                    // Income/Investment columns: up arrow if diff > 0, down if < 0
                                    // Expense columns: up arrow if diff < 0, down if > 0
                                    const incomeKeys = [
                                      "Paycheck",
                                      "USA_Salary",
                                      "Residential_Rent",
                                      "House_Advance",
                                      "GeniusNexa_Income",
                                      "Renunumari_Income",
                                      "Gpay_Cashback",
                                      "Savings_Interest",
                                      "Dividend_Income",
                                      "Tax_Refund",
                                      "Handcash",
                                      "Other_Income",
                                      // Investments
                                      "Mutual_Fund",
                                      "Pension",
                                      "Stock",
                                      "ICICI_Life_Insurance",
                                      "LIC",
                                    ];

                                    const expenseKeys = [
                                      // Debt
                                      "Chitfund",
                                      "Personal_Loan",
                                      "HandCash_Repayment",

                                      // House
                                      "EBBills",
                                      "PropertyTax",

                                      // Mobile
                                      "Mobile_Recharge",
                                      "BSNL_Bills",

                                      // Family
                                      "Personal_Donations",
                                      "Pooja_Expenses",
                                      "To_Prakish",
                                      "Diwali_Gift_To_Family",
                                      "To_Amma",

                                      // Other
                                      "Abacus",
                                      "Chess_Fees",
                                      "ATM_Withdraw",
                                      "Cab_Expenses",
                                      "Taxfiling_Expenses",
                                      "GeniusNexa_Bills",
                                      "Credit_Card_Payment",
                                      "Other_Expenses",
                                      "Billing_Errors",
                                    ];

                                    if (
                                      incomeKeys.includes(col.key) ||
                                      col.key === "year" ||
                                      col.key === "month"
                                    ) {
                                      arrow = diff > 0 ? "▲" : "▼";
                                      diffColor =
                                        diff > 0
                                          ? "text-green-600"
                                          : "text-red-600";
                                    } else if (expenseKeys.includes(col.key)) {
                                      arrow = diff < 0 ? "▲" : "▼";
                                      diffColor =
                                        diff < 0
                                          ? "text-green-600"
                                          : "text-red-600";
                                    }
                                  }
                                }
                              }
                              return (
                                <td
                                  key={col.key}
                                  className={`border border-gray-200 px-2 py-1 whitespace-nowrap text-${
                                    colIdx < 2
                                      ? "left font-semibold"
                                      : "right text-blue-900 font-bold"
                                  } ${
                                    colIdx === 0
                                      ? "sticky left-0 bg-white z-20"
                                      : colIdx === 1
                                        ? "sticky left-[80px] bg-white z-20"
                                        : ""
                                  } ${
                                    isFigure && value !== "$0.00"
                                      ? "cursor-pointer hover:bg-blue-50"
                                      : ""
                                  }`}
                                  style={{
                                    minWidth: 80,
                                    boxShadow:
                                      colIdx < 2
                                        ? "2px 0 8px -2px rgba(59,130,246,0.10)"
                                        : undefined,
                                  }}
                                  onClick={
                                    isFigure && value !== "$0.00"
                                      ? () =>
                                          handleCellClick(
                                            col.key,
                                            row["year"],
                                            row["month"],
                                          )
                                      : undefined
                                  }
                                  title={
                                    isFigure && value !== "$0.00"
                                      ? `View details for ${col.label}`
                                      : undefined
                                  }
                                >
                                  {value}
                                  {diff !== null && isFigure && (
                                    <span
                                      className={`ml-1 text-xs ${diffColor}`}
                                      title={`Change: ${diff}`}
                                    >
                                      {" "}
                                      {arrow} {formatCurrency(diff)}
                                    </span>
                                  )}
                                </td>
                              );
                            })}
                            {/* Modal for details */}
                            {modalOpen && (
                              <div
                                className="fixed inset-0 z-50 flex items-center justify-center"
                                style={{ background: "rgba(245,247,250,0.85)" }}
                              >
                                <div className="bg-white rounded-lg shadow-xl w-[1100px] p-0 border border-gray-300 relative">
                                  <button
                                    className="absolute top-3 right-4 text-gray-500 hover:text-gray-700 text-2xl font-bold"
                                    onClick={() => setModalOpen(false)}
                                    aria-label="Close"
                                    style={{ lineHeight: 1 }}
                                  >
                                    &times;
                                  </button>
                                  <div className="px-6 pt-5 pb-2 border-b bg-gray-50 rounded-t-lg">
                                    <h3 className="text-lg font-bold text-blue-700">
                                      {modalTitle}
                                    </h3>
                                  </div>
                                  <div className="px-6 pb-6 pt-2">
                                    {modalLoading ? (
                                      <div className="text-center py-8 text-blue-600 font-semibold">
                                        Loading...
                                      </div>
                                    ) : modalData.length === 0 ? (
                                      <div className="text-center py-8 text-gray-500">
                                        No records found.
                                      </div>
                                    ) : (
                                      <div
                                        style={{
                                          maxHeight: "340px",
                                          overflowY: "auto",
                                        }}
                                      >
                                        <table
                                          className="w-full text-sm border border-gray-300 rounded-lg"
                                          style={{ tableLayout: "fixed" }}
                                        >
                                          <thead>
                                            <tr className="bg-gray-100">
                                              <th className="border px-3 py-2 text-left w-[110px]">
                                                Date
                                              </th>
                                              <th className="border px-3 py-2 text-left w-[110px]">
                                                Transaction
                                              </th>
                                              <th className="border px-3 py-2 text-left w-[220px]">
                                                Description
                                              </th>
                                              <th className="border px-3 py-2 text-right w-[110px]">
                                                Amount
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {modalData.map((item, i) => (
                                              <tr
                                                key={i}
                                                className={
                                                  i % 2 === 0
                                                    ? "bg-white"
                                                    : "bg-gray-50"
                                                }
                                              >
                                                <td className="border px-3 py-2 text-left font-mono text-xs">
                                                  {item.transaction_date ||
                                                    item.TransactionDate ||
                                                    item.date ||
                                                    "-"}
                                                </td>
                                                <td className="border px-3 py-2 text-left">
                                                  {item.transaction_type ||
                                                    item.type ||
                                                    "-"}
                                                </td>
                                                <td className="border px-3 py-2 text-left">
                                                  {item.description ||
                                                    item.Description ||
                                                    "-"}
                                                </td>
                                                <td className="border px-3 py-2 text-right font-bold text-blue-900">
                                                  {formatIndCurrency(
                                                    item.amount || item.Amount,
                                                  )}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </tr>
                        ))}
                        {/* Original Total Row */}
                        {IndtotalRow && (
                          <tr className="bg-yellow-100 font-bold sticky bottom-0 z-40">
                            {Indcolumns.map((col, colIdx) => (
                              <td
                                key={col.key}
                                className={`border border-gray-200 px-2 py-1 text-${
                                  colIdx < 2
                                    ? "left"
                                    : "right text-blue-900 font-bold"
                                } ${
                                  colIdx === 0
                                    ? "sticky left-0 z-40 bg-yellow-100"
                                    : colIdx === 1
                                      ? "sticky left-[80px] z-40 bg-yellow-100"
                                      : ""
                                }`}
                                style={{
                                  minWidth: 80,
                                  boxShadow:
                                    colIdx < 2
                                      ? "2px 0 8px -2px rgba(59,130,246,0.10)"
                                      : undefined,
                                }}
                              >
                                {colIdx === 0
                                  ? "Total"
                                  : colIdx === 1
                                    ? ""
                                    : formatIndCurrency(IndtotalRow[col.key])}
                              </td>
                            ))}
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Other Tab Contents */}
          {selectedTab === "Health Record" && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  🏥 Health Records
                </h2>
                <p className="text-gray-600">
                  Track and manage personal health metrics and weight records
                </p>
              </div>
              <PersonWeightsForm />
            </div>
          )}

          {selectedTab === "Investment Details" &&
            (() => {
              // Investment Portfolio Analytics - Industry Standard Calculations
              // Get current portfolio market value (in Lakhs)
              const currentData = getLatestPortfolioData();
              const currentMarketValueInLakhs = Object.values(
                currentData,
              ).reduce((sum, value) => sum + value, 0);
              const currentMarketValueInCrores = (
                currentMarketValueInLakhs / 100
              ).toFixed(2);

              // Get current month/year from latest data
              const latestDataEntry =
                monthlyInvestmentData.length > 0
                  ? monthlyInvestmentData.reduce((latest, current) => {
                      const currentDate = new Date(
                        current.year,
                        current.month - 1,
                      );
                      const latestDate = new Date(
                        latest.year,
                        latest.month - 1,
                      );
                      return currentDate > latestDate ? current : latest;
                    }, monthlyInvestmentData[0])
                  : null;

              const currentMonth = latestDataEntry
                ? `${
                    [
                      "Jan",
                      "Feb",
                      "Mar",
                      "Apr",
                      "May",
                      "Jun",
                      "Jul",
                      "Aug",
                      "Sep",
                      "Oct",
                      "Nov",
                      "Dec",
                    ][latestDataEntry.month - 1]
                  } ${latestDataEntry.year}`
                : `${
                    [
                      "Jan",
                      "Feb",
                      "Mar",
                      "Apr",
                      "May",
                      "Jun",
                      "Jul",
                      "Aug",
                      "Sep",
                      "Oct",
                      "Nov",
                      "Dec",
                    ][new Date().getMonth()]
                  } ${new Date().getFullYear()}`;

              return (
                <div id="portfolio-analytics" className="space-y-6">
                  {/* Enhanced Investment Header */}
                  <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl shadow-lg p-8 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-3xl font-bold mb-2 flex items-center text-white">
                          <span className="text-4xl mr-3">📊</span>
                          Investment Portfolio Analytics
                        </h2>
                        <p className="text-white/90 text-lg font-medium">
                          Track monthly performance, growth trends, and
                          portfolio analysis
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="bg-white/30 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                          <p className="text-sm text-white font-semibold mb-1">
                            Current Portfolio Value
                          </p>
                          <p className="text-2xl font-bold text-white">
                            ₹{currentMarketValueInCrores}Cr
                          </p>
                          <p className="text-xs text-white/90 font-medium">
                            As of {currentMonth}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Monthly Growth Trends - Full Width */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                      <span className="text-xl mr-2">📈</span>
                      Monthly Investment Portfolio Trends (2024-2025)
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {getMonthlyTrends().map((data, index) => {
                        // Convert all values to Lakhs for proper comparison
                        const parseValue = (valueStr) => {
                          const cleanValue = valueStr.replace(/[₹,]/g, "");
                          if (cleanValue.includes("Cr")) {
                            return (
                              parseFloat(cleanValue.replace("Cr", "")) * 100
                            ); // Convert Cr to L
                          } else if (cleanValue.includes("L")) {
                            return parseFloat(cleanValue.replace("L", "")); // Already in L
                          }
                          return parseFloat(cleanValue);
                        };

                        const currentValue = parseValue(data.total);
                        const maxValue = 136; // 1.36Cr = 136L for scaling
                        const percentage = Math.max(
                          5,
                          (currentValue / maxValue) * 100,
                        ); // Minimum 5% for visibility

                        return (
                          <div
                            key={index}
                            className="bg-gray-50 rounded-lg p-4 overflow-hidden"
                          >
                            <div className="flex items-center justify-between mb-3 gap-2">
                              <h4 className="text-sm font-bold text-gray-800 truncate">
                                {data.month.replace(" 2025", "")}
                              </h4>
                              <span className="text-xs font-bold text-white bg-green-600 px-2 py-1 rounded whitespace-nowrap flex-shrink-0">
                                {data.growth}
                              </span>
                            </div>
                            <div className="space-y-2 mb-3">
                              <div className="flex justify-between text-xs">
                                <span className="text-gray-600">
                                  India Stock:
                                </span>
                                <span className="font-semibold">
                                  {data.indiaStock}
                                </span>
                              </div>
                              <div className="flex justify-between text-xs">
                                <span className="text-gray-600">
                                  India Bond:
                                </span>
                                <span className="font-semibold">
                                  {data.indiaBond}
                                </span>
                              </div>
                              <div className="flex justify-between text-xs">
                                <span className="text-gray-600">
                                  USA Stock:
                                </span>
                                <span className="font-semibold">
                                  {data.usaStock}
                                </span>
                              </div>
                              <div className="flex justify-between text-xs">
                                <span className="text-gray-600">
                                  USA Fixed:
                                </span>
                                <span className="font-semibold">
                                  {data.usaFixed}
                                </span>
                              </div>
                            </div>
                            <div className="border-t pt-2">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-bold text-gray-900">
                                  Total:
                                </span>
                                <span className="text-sm font-bold text-gray-900">
                                  {data.total}
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-3">
                                <div
                                  className={`h-3 rounded-full ${data.color} transition-all duration-700`}
                                  style={{ width: `${percentage}%` }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Portfolio Composition */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <span className="text-xl mr-2">🥧</span>
                      Current Portfolio Mix ({currentMonth})
                    </h3>
                    <div className="space-y-4">
                      {(() => {
                        const portfolioData = getLatestPortfolioData();
                        const total = Object.values(portfolioData).reduce(
                          (sum, val) => sum + val,
                          0,
                        );

                        const compositions = [
                          {
                            name: "India Stock",
                            value: `₹${portfolioData.indiaStock.toFixed(1)}L`,
                            percentage:
                              total > 0
                                ? (
                                    (portfolioData.indiaStock / total) *
                                    100
                                  ).toFixed(1)
                                : 0,
                            color: "bg-orange-500",
                          },
                          {
                            name: "India Bond",
                            value: `₹${portfolioData.indiaBond.toFixed(1)}L`,
                            percentage:
                              total > 0
                                ? (
                                    (portfolioData.indiaBond / total) *
                                    100
                                  ).toFixed(1)
                                : 0,
                            color: "bg-orange-500",
                          },
                          {
                            name: "USA Stock",
                            value: `₹${portfolioData.usaStock.toFixed(1)}L`,
                            percentage:
                              total > 0
                                ? (
                                    (portfolioData.usaStock / total) *
                                    100
                                  ).toFixed(1)
                                : 0,
                            color: "bg-blue-500",
                          },
                          {
                            name: "USA Fixed",
                            value: `₹${portfolioData.usaFixed.toFixed(1)}L`,
                            percentage:
                              total > 0
                                ? (
                                    (portfolioData.usaFixed / total) *
                                    100
                                  ).toFixed(1)
                                : 0,
                            color: "bg-green-500",
                          },
                          {
                            name: "ICICI PruLife",
                            value: `₹${portfolioData.iciciprulife.toFixed(1)}L`,
                            percentage:
                              total > 0
                                ? (
                                    (portfolioData.iciciprulife / total) *
                                    100
                                  ).toFixed(1)
                                : 0,
                            color: "bg-purple-500",
                          },
                        ];

                        return compositions.map((item, index) => (
                          <div key={index} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <div
                                  className={`w-4 h-4 rounded-full ${item.color}`}
                                ></div>
                                <span className="text-sm font-medium text-gray-700">
                                  {item.name}
                                </span>
                              </div>
                              <div className="text-right">
                                <span className="text-sm font-bold text-gray-900">
                                  {item.value}
                                </span>
                              </div>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full ${item.color} transition-all duration-700`}
                                style={{ width: `${item.percentage}%` }}
                              ></div>
                            </div>
                            <div className="text-xs text-gray-500">
                              {item.percentage}% of portfolio
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>

                  {/* Main Investment Form Section */}
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                          <h3 className="text-xl font-semibold text-gray-900">
                            Monthly Investment Data Entry
                          </h3>
                        </div>
                        <div className="flex items-center space-x-4">
                          <button
                            onClick={handleViewAnalytics}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2"
                          >
                            <FontAwesomeIcon icon={faChartLine} />
                            <span>View Analytics</span>
                          </button>
                          <button
                            onClick={handleExportData}
                            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2"
                          >
                            <FontAwesomeIcon icon={faFileExport} />
                            <span>Export Data</span>
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="p-6">
                      <MonthlyCorpsForm
                        onDataUpdate={fetchMonthlyInvestmentData}
                      />
                    </div>
                  </div>
                </div>
              );
            })()}

          {selectedTab === "Bank Records" && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  🏦 Bank Transaction Management
                </h2>
                <p className="text-gray-600">
                  Update and categorize bank transaction records
                </p>
              </div>
              <UpdateTransactionCategory />
            </div>
          )}

          {selectedTab === "CreditCard Records" && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  💳 Credit Card Expense Management
                </h2>
                <p className="text-gray-600">
                  Manage and categorize credit card expense records
                </p>
              </div>
              <UpdateExpenseCategory />
            </div>
          )}

          {selectedTab === "Summary View" && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  📊 Comprehensive Financial Summary
                </h2>
                <p className="text-gray-600">
                  View consolidated financial overview and insights
                </p>
              </div>
              <SummaryView />
            </div>
          )}

          {selectedTab === "Goals Management" && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <GoalsManagement />
            </div>
          )}
        </div>
      ) : (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-red-100">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center border border-red-200">
            <div className="bg-red-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <FontAwesomeIcon icon={faEye} className="text-2xl text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-red-900 mb-2">
              Access Denied
            </h2>
            <p className="text-red-600">
              Administrator privileges required to access this financial
              dashboard
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionSummaryByYears;
